import { useState } from 'react';
import axios from 'axios';
import {
  Button, Modal, Row, Col, Typography,
  Alert, App as AntApp, Flex, Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { fmtDataBr } from '../../utils/dataFmt';
import { BANCO_LABEL } from './types';
import { getToken, fmt } from './utils';

const { Text } = Typography;

// Modal "Importar arquivo OFX" — upload, parse no backend (idempotente via FITID),
// retorna sumário (banco, conta, período, novas/duplicadas, auto-classif).
export default function ModalUploadOFX({ onFechar, onSucesso }: { onFechar: () => void; onSucesso: () => void }) {
  const { message } = AntApp.useApp();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<any>(null);

  async function enviar() {
    if (!arquivo) return;
    setEnviando(true); setResultado(null);
    try {
      const buf = await arquivo.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = '';
      for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
      const arquivo_base64 = btoa(bin);
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/ofx/upload`,
        { arquivo_base64, nome: arquivo.name },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setResultado(d);
      message.success('Importação concluída.');
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message);
    } finally { setEnviando(false); }
  }

  return (
    <Modal open onCancel={onFechar} footer={null} title="📤 Importar arquivo OFX" width={600}>
      {!resultado ? (
        <>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 14 }}>
            Baixe o extrato em formato <strong>OFX</strong> do internet banking
            (Inter, Itaú, SICOOB). Suporta OFX 1.x SGML. Re-importar o mesmo arquivo é seguro — duplicatas são ignoradas pelo FITID.
          </Text>
          <Upload.Dragger
            accept=".ofx,.OFX" multiple={false}
            beforeUpload={(file) => { setArquivo(file); return false; }}
            onRemove={() => setArquivo(null)}
            fileList={arquivo ? [{ uid: '1', name: arquivo.name, size: arquivo.size } as any] : []}
          >
            <p><UploadOutlined style={{ fontSize: 32, color: '#0930D0' }} /></p>
            <p>Clique ou arraste o arquivo OFX aqui</p>
          </Upload.Dragger>
          <Flex justify="flex-end" gap={8} style={{ marginTop: 16 }}>
            <Button onClick={onFechar}>Cancelar</Button>
            <Button type="primary" icon={<UploadOutlined />} disabled={!arquivo} loading={enviando} onClick={enviar}
              style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
              Importar
            </Button>
          </Flex>
        </>
      ) : (
        <div>
          <Alert type="success" message="Importação concluída" showIcon style={{ marginBottom: 14 }} />
          <Row gutter={[8, 6]} style={{ fontSize: 12 }}>
            <Col span={10}><Text type="secondary">Banco</Text></Col>
            <Col span={14}><Text strong>{BANCO_LABEL[resultado.banco] || resultado.banco}</Text></Col>
            <Col span={10}><Text type="secondary">Conta</Text></Col>
            <Col span={14}>{resultado.conta_label}</Col>
            <Col span={10}><Text type="secondary">Período</Text></Col>
            <Col span={14}>
              {resultado.periodo?.inicio ? fmtDataBr(resultado.periodo.inicio) : '—'}
              {' a '}
              {resultado.periodo?.fim ? fmtDataBr(resultado.periodo.fim) : '—'}
            </Col>
            <Col span={10}><Text type="secondary">Saldo final</Text></Col>
            <Col span={14}><Text strong style={{ color: '#020D7F' }}>{resultado.saldo_final != null ? fmt(resultado.saldo_final) : '—'}</Text></Col>
            <Col span={24}><div style={{ borderTop: '0.5px dashed #eee', margin: '6px 0' }} /></Col>
            <Col span={10}><Text type="secondary">Transações no arquivo</Text></Col>
            <Col span={14}>{resultado.total_no_arquivo}</Col>
            <Col span={10}><Text style={{ color: '#0a7f4a' }}>✓ Novas inseridas</Text></Col>
            <Col span={14}><Text strong style={{ color: '#0a7f4a' }}>{resultado.inseridas}</Text></Col>
            <Col span={10}><Text type="secondary">↺ Duplicadas</Text></Col>
            <Col span={14}>{resultado.duplicadas}</Col>
            <Col span={10}><Text style={{ color: '#7C3AED' }}>🤖 Auto-classificadas</Text></Col>
            <Col span={14}><Text strong>{resultado.classificadas_por_regra}</Text></Col>
            {resultado.erros > 0 && (
              <>
                <Col span={10}><Text style={{ color: '#B91626' }}>⚠ Erros</Text></Col>
                <Col span={14}><Text strong style={{ color: '#B91626' }}>{resultado.erros}</Text></Col>
              </>
            )}
          </Row>
          <Alert
            type="info" showIcon style={{ marginTop: 16 }}
            message={<Text style={{ fontSize: 11 }}>💡 Próximo passo: classifique as transações <strong>Não classificadas</strong> manualmente. O sistema aprende regras a partir delas.</Text>}
          />
          <Button block type="primary" style={{ marginTop: 14, background: '#020D7F', borderColor: '#020D7F' }}
            onClick={onSucesso}>Ver extrato →</Button>
        </div>
      )}
    </Modal>
  );
}
